import Alpine from './alpine'

/**
 * The search overlay.
 *
 * Opens on click or Ctrl/Cmd-K, searches as you type, and narrows by resource when a
 * chip is picked. Enter goes to the full results page, because a drop-down showing
 * five per group is for jumping straight to something you can already see.
 *
 * Recent searches live in this browser rather than on the server. They are a
 * convenience for the person sitting here, not a fact about the shop, and putting them
 * in the database would mean a merchant's half-typed customer name syncing to every
 * device they own.
 */
Alpine.data('adminSearch', (endpoint = '', filters = []) => ({
    open: false,
    term: '',
    type: '',
    filters,
    groups: [],
    total: 0,
    loading: false,
    recent: [],
    choosing: false,

    /** Two characters, or every keystroke asks the server for the whole catalogue. */
    get searching() {
        return this.term.trim().length >= 2
    },

    /** The chosen resource, as a token in the field rather than a chip above it. */
    get picked() {
        return this.filters.find((filter) => filter.key === this.type) || null
    },

    init() {
        this.recent = this.readRecent()
    },

    show() {
        this.open = true

        // The field has to exist before it can be focused, and x-show only
        // reveals it on the next tick.
        this.$nextTick(() => this.$refs.field?.focus())
    },

    close() {
        this.open = false
        this.choosing = false
    },

    pick(key) {
        this.type = this.type === key ? '' : key
        this.choosing = false
        this.search()

        // Narrowing is something you do mid-query; the caret belongs back in
        // the field, not on the control you just used.
        this.$nextTick(() => this.$refs.field?.focus())
    },

    /** The "Show N more" row under a group: the same query, that group only. */
    seeAll(key) {
        const term = this.term.trim()

        if (term === '') return

        this.remember(term)

        window.location = `${endpoint}?q=${encodeURIComponent(term)}&type=${encodeURIComponent(key)}`
    },

    /*
     * Debounced, and every answer carries the request that asked for it. Without
     * that, a slow search for "ca" can land after a fast one for "cake" and replace
     * the right results with stale ones.
     */
    search() {
        clearTimeout(this.timer)

        if (!this.searching) {
            this.groups = []
            this.total = 0
            this.loading = false

            return
        }

        this.loading = true
        this.timer = setTimeout(() => this.fetch(), 200)
    },

    async fetch() {
        const asked = ++this.sequence

        try {
            const url = `${endpoint}?q=${encodeURIComponent(this.term)}&type=${encodeURIComponent(this.type)}`
            const response = await fetch(url, { headers: { Accept: 'application/json' } })

            if (!response.ok) throw new Error(`Search failed (${response.status})`)

            const payload = await response.json()

            if (asked !== this.sequence) return

            this.groups = payload.groups || []
            this.total = payload.total || 0
        } catch (error) {
            if (asked === this.sequence) {
                this.groups = []
                this.total = 0
            }
        } finally {
            if (asked === this.sequence) this.loading = false
        }
    },

    /** Enter, or the "see all" row. */
    submit() {
        const term = this.term.trim()

        if (term === '') return

        this.remember(term)

        window.location = `${endpoint}?q=${encodeURIComponent(term)}&type=${encodeURIComponent(this.type)}`
    },

    follow(url) {
        this.remember(this.term.trim())
        window.location = url
    },

    // Recent searches -------------------------------------------------------

    remember(term) {
        if (!term) return

        const next = [term, ...this.recent.filter((item) => item !== term)].slice(0, 5)

        this.recent = next
        this.writeRecent(next)
    },

    clearRecent() {
        this.recent = []
        this.writeRecent([])
    },

    replay(term) {
        this.term = term
        this.search()
    },

    /*
     * Storage can throw in a private window and can come back with whatever was
     * there before, so both directions are guarded and anything unexpected is
     * treated as "no history" rather than taken on trust.
     */
    readRecent() {
        try {
            const raw = JSON.parse(window.localStorage.getItem('admin.search.recent') || '[]')

            return Array.isArray(raw) ? raw.filter((item) => typeof item === 'string').slice(0, 5) : []
        } catch (error) {
            return []
        }
    },

    writeRecent(value) {
        try {
            window.localStorage.setItem('admin.search.recent', JSON.stringify(value))
        } catch (error) {
            // A browser that will not store it still searches perfectly well.
        }
    },

    timer: null,
    sequence: 0,
}))
